import api from "../utils/api";
import type { CommandStatus } from "../store/admin/deviceCommandsStore";

export const DASHCAM_CAMERAS = ["dms", "adas"] as const;

export type DashcamCamera = (typeof DASHCAM_CAMERAS)[number];

export const DASHCAM_PROTOCOLS = ["whep", "wss", "rtmp"] as const;

export type DashcamProtocol = (typeof DASHCAM_PROTOCOLS)[number];

export const DEFAULT_DASHCAM_PROTOCOL: DashcamProtocol = "whep";

const DEFAULT_DASHCAM_WHEP_BASE_URL = "https://video.34.26.85.83.sslip.io";

export interface DashcamConnection {
  stream_id?: string | null;
  stream_url?: string | null;
  channels?: string[] | null;
  protocol?: string | null;
  adas_enabled?: boolean | null;
  dms_enabled?: boolean | null;
}

export interface DashcamUnit {
  plate?: string;
  name: string;
  deviceId?: string | null;
  imei?: string | null;
  dashCam?: DashcamConnection | null;
}

export const normalizeUnitId = (value?: string | null): string =>
  (value ?? "").replace(/\s+/g, "").toUpperCase();

export const dashcamStreamUrl = (dashCam?: DashcamConnection | null): string | null =>
  dashCam?.stream_url?.trim() || null;

export const dashcamProtocol = (dashCam?: DashcamConnection | null): DashcamProtocol => {
  const value = dashCam?.protocol?.trim().toLowerCase();
  return DASHCAM_PROTOCOLS.find((protocol) => protocol === value) ?? DEFAULT_DASHCAM_PROTOCOL;
};

export const isDashcamProtocolPlayable = (dashCam?: DashcamConnection | null): boolean =>
  dashcamProtocol(dashCam) !== "rtmp";

const CHANNEL_TO_CAMERA: Record<string, DashcamCamera> = {
  out: "adas",
  front: "adas",
  in: "dms",
  inward: "dms",
  adas: "adas",
  dms: "dms",
};

export const dashcamCameras = (dashCam?: DashcamConnection | null): DashcamCamera[] => {
  const declared = new Set(
    (dashCam?.channels ?? [])
      .map((channel) => CHANNEL_TO_CAMERA[channel.trim().toLowerCase()])
      .filter(Boolean),
  );
  return DASHCAM_CAMERAS.filter((camera) => declared.has(camera));
};

const CAMERA_TO_CHANNEL: Record<DashcamCamera, string> = {
  adas: "out",
  dms: "in",
};

export const camerasToChannels = (cameras: DashcamCamera[]): string[] => [
  ...new Set(["out", ...cameras.map((camera) => CAMERA_TO_CHANNEL[camera])]),
];

export const dashcamHasAdas = (dashCam?: DashcamConnection | null): boolean =>
  dashCam?.adas_enabled === true;

export const dashcamHasDms = (dashCam?: DashcamConnection | null): boolean =>
  dashCam?.dms_enabled === true;

export const dashcamCameraAvailable = (
  dashCam: DashcamConnection | null | undefined,
  camera: DashcamCamera,
): boolean => dashcamCameras(dashCam).includes(camera);

export const defaultDashcamCamera = (
  dashCam?: DashcamConnection | null,
): DashcamCamera | null => {
  const existen = dashcamCameras(dashCam);
  if (existen.includes("dms")) return "dms";
  if (existen.includes("adas")) return "adas";
  return null;
};

export const isDashcamStreamConfigured = (dashCam?: DashcamConnection | null): boolean => {
  if (dashcamProtocol(dashCam) === "whep") return true;
  return Boolean(dashcamStreamUrl(dashCam));
};

const dashcamWhepBaseUrl = (): string =>
  (import.meta.env.VITE_DASHCAM_WHEP_BASE_URL as string | undefined)?.trim() ||
  DEFAULT_DASHCAM_WHEP_BASE_URL;

const DASHCAM_WHEP_CHANNEL_INDEX = {
  adas: 0,
  dms: 1,
} as const satisfies Record<DashcamCamera, number>;

export const dashcamWhepStreamUrl = (imei: string, camera: DashcamCamera | null): string => {
  const channel = camera ? DASHCAM_WHEP_CHANNEL_INDEX[camera] : 0;
  return `${dashcamWhepBaseUrl()}/live/${channel}/${imei}/`;
};

export const dashcamWhepUrl = (streamUrl: string): string =>
  new URL("whep", streamUrl.endsWith("/") ? streamUrl : `${streamUrl}/`).toString();

const LIVE_VIDEO_ON_CODE = "live_video_on";

interface BrandCommandEntry {
  code?: string;
  enabled?: boolean;
  sms_enabled?: boolean;
}

const smsSupportByBrand = new Map<string, Promise<boolean | null>>();

const fetchSmsSupport = (brandId: string): Promise<boolean | null> =>
  api
    .get(`/commandsList?brand_id=${encodeURIComponent(brandId)}&limit=50`)
    .then((res) => {
      const commands = (res.data?.data ?? []) as BrandCommandEntry[];
      const command = commands.find((entry) => entry.code === LIVE_VIDEO_ON_CODE);
      return command ? command.enabled !== false && command.sms_enabled === true : null;
    })
    .catch(() => null);

export const dashcamSmsSupported = (brandId: string): Promise<boolean | null> => {
  const cached = smsSupportByBrand.get(brandId);
  if (cached) return cached;

  const request = fetchSmsSupport(brandId);
  smsSupportByBrand.set(brandId, request);
  void request.then((value) => value === null && smsSupportByBrand.delete(brandId));

  return request;
};

export const DASHCAM_COMMAND_TRANSPORTS = ["gprs", "sms"] as const;

export type DashcamCommandTransport = (typeof DASHCAM_COMMAND_TRANSPORTS)[number];

const LIVE_VIDEO_AWAIT_MS = 8000;

export interface DashcamCommand {
  request_id?: string;
  device_id?: string;
  company_id?: string;
  vehicle_id?: string;
  remolque_id?: string;
  imei?: string;
  brand_id?: string;
  brand?: string;
  model?: string;
  command_doc_id?: string;
  command_code?: string;
  command_name?: string;
  status?: CommandStatus;
  error?: string | null;
  raw_request?: Record<string, unknown> | null;
  raw_response?: Record<string, unknown> | null;
  sent_at?: number | null;
  finished_at?: number | null;
}

export interface DashcamCommandResult {
  ok?: boolean;
  command?: DashcamCommand;
  wait_timeout?: boolean;
}

export interface DashcamCommandTarget {
  deviceId?: string | null;
  imei?: string | null;
  createdByUserId?: string | null;
}

export interface DashcamLiveVideoOptions extends DashcamCommandTarget {
  transport?: DashcamCommandTransport;
  smsPassword?: string | null;
}

const commandTarget = ({ deviceId, imei }: DashcamCommandTarget) => ({
  ...(deviceId ? { deviceId } : {}),
  ...(imei ? { imei } : {}),
});

export const requestDashcamLiveVideo = ({
  deviceId,
  imei,
  transport = "gprs",
  smsPassword,
  createdByUserId,
}: DashcamLiveVideoOptions): Promise<DashcamCommandResult | undefined> => {
  if (!deviceId && !imei) return Promise.resolve(undefined);

  const target = commandTarget({ deviceId, imei });

  return transport === "sms"
    ? api
        .post("/sendCommandSms", {
          ...target,
          command_code: "live_video_on",
          ...(smsPassword ? { password_sms: smsPassword } : {}),
          args: {},
          ...(createdByUserId ? { created_by_user_id: createdByUserId } : {}),
        })
        .then((res) => res.data as DashcamCommandResult)
    : api
        .post("/sendCommand", {
          ...target,
          command_code: "live_video_on",
          args: {},
          await_ms: LIVE_VIDEO_AWAIT_MS,
          ...(createdByUserId ? { created_by_user_id: createdByUserId } : {}),
        })
        .then((res) => res.data as DashcamCommandResult);
};

export const requestDashcamLiveVideoOff = ({
  deviceId,
  imei,
  createdByUserId,
}: DashcamCommandTarget): Promise<DashcamCommandResult | undefined> => {
  if (!deviceId && !imei) return Promise.resolve(undefined);

  return api
    .post("/sendCommand", {
      ...commandTarget({ deviceId, imei }),
      command_code: "live_video_off",
      args: {},
      await_ms: LIVE_VIDEO_AWAIT_MS,
      ...(createdByUserId ? { created_by_user_id: createdByUserId } : {}),
    })
    .then((res) => res.data as DashcamCommandResult);
};

export const dashcamUnitId = (unit: DashcamUnit): string => {
  const streamId = unit.dashCam?.stream_id?.trim();
  return streamId || normalizeUnitId(unit.plate ?? unit.name);
};

export const dashcamUnitAliases = (unit: DashcamUnit): string[] => [
  ...new Set(
    [unit.dashCam?.stream_id, unit.plate, unit.name].map(normalizeUnitId).filter(Boolean),
  ),
];
